import React, {useMemo, useState} from "react";
import {connect} from "react-redux";
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, Table, TableBody, TableCell,
    TableHead, TableRow, Typography,
} from "@mui/material";
import {updateOsdOrganisationConfig} from "../../../redux_actions/osd.viewer.config.action";
import {getZoomScaleForSlide} from "../utils/map_utils";
import {getMagMapping, getObjectiveKey, getScanKind, magKey, SCAN_KINDS} from "../openseadragon/osdSettings";
import {ZOOM_PRESET_CHOICES} from "../openseadragon/utils/zoomPresets";

const NATIVE = "native";

/**
 * Superuser editor for the organisation's magnification labels. A mapping applies to every slide
 * of the same scan kind and objective; the current level is highlighted.
 */
const MagMappingDialog = ({slide, zoomLevels, currentLevel, osdViewerConfig, onClose, dispatch}) => {
    const nativeScale = useMemo(() => getZoomScaleForSlide(slide, zoomLevels), [slide, zoomLevels]);
    const saved = getMagMapping(slide, osdViewerConfig);
    const [draft, setDraft] = useState(() => ({...saved}));
    const [saving, setSaving] = useState(false);

    const rows = [];
    nativeScale.forEach((label, idx) => {
        const key = magKey(label);
        if (!rows.some(row => row.key === key)) rows.push({key, label, isCurrent: idx === currentLevel});
        else if (idx === currentLevel) rows.find(row => row.key === key).isCurrent = true;
    });

    const choices = [...new Set([...ZOOM_PRESET_CHOICES, ...Object.values(saved).filter(Number.isFinite)])]
        .sort((a, b) => a - b);

    const changes = {};
    rows.forEach(({key}) => {
        if ((draft[key] ?? null) !== (saved[key] ?? null)) changes[key] = draft[key] ?? null;
    });

    const save = () => {
        setSaving(true);
        const body = {mag_mapping: {[getScanKind(slide)]: {[getObjectiveKey(slide)]: changes}}};
        Promise.resolve(dispatch(updateOsdOrganisationConfig(body))).finally(onClose);
    };

    const scanKindLabel = getScanKind(slide) === SCAN_KINDS.IMPORTED ? "Imported slides" : "Morphle scans";
    const objective = getObjectiveKey(slide).replace("_", " ");

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Magnification mapping</DialogTitle>
            <DialogContent>
                <Typography variant="body2" gutterBottom>
                    {scanKindLabel}, {objective} objective. Applies to the whole organisation.
                </Typography>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Native</TableCell>
                            <TableCell>Shown as</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map(({key, label, isCurrent}) => (
                            <TableRow key={key} selected={isCurrent}>
                                <TableCell>{label}×{isCurrent ? " (current)" : ""}</TableCell>
                                <TableCell>
                                    <Select size="small" variant="standard" value={draft[key] ?? NATIVE}
                                            onChange={e => setDraft({
                                                ...draft,
                                                [key]: e.target.value === NATIVE ? undefined : e.target.value,
                                            })}>
                                        <MenuItem value={NATIVE}>Same as native</MenuItem>
                                        {choices.map(choice =>
                                            <MenuItem key={choice} value={choice}>{choice}×</MenuItem>)}
                                    </Select>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={saving || !Object.keys(changes).length}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const mapStateToProps = (state) => ({osdViewerConfig: state.osdViewerConfigReducer});

export default connect(mapStateToProps)(MagMappingDialog);
